/* Knobs.co — post-wizard journey tracker (site-wide, theme.liquid).
   ─────────────────────────────────────────────────────────────────
   After someone completes the hardware wizard, knobs-wizard-results.js writes
   a "journey" record to localStorage (kw.journey.v2). This script — loaded on
   EVERY page — then follows the visitor for the REST OF THEIR VISIT: pages
   viewed, add-to-carts, checkout start, and the results-page CTA clicks
   already recorded, each stamped with seconds-since-completion. ONE summary
   beacons to the knobs-trade-apply worker → team email when the journey ends:
     - they leave the site (pagehide with no internal-nav flag), or
     - they start checkout (theme scripts don't run on Shopify checkout pages,
       so this is the last observable moment — recorded as 'started checkout'), or
     - the 45-min cap fires (idle open tab / marathon session), or
     - they come back to the storefront with a journey older than the cap
       (recovers mobile pagehides that never fired).

   Dormant by design: when no unsent journey exists (99.9% of page views) it
   reads one localStorage key and returns — no listeners, no fetch patch.
   Every hook is try/caught; this must never affect shopping. */
(function () {
  'use strict';
  var KEY = 'kw.journey.v2';
  var NAV_FLAG = 'kw.nav';
  var ENDPOINT = 'https://knobs-trade-apply.dmason-494.workers.dev/';
  var CAP_MS = 45 * 60 * 1000;      // hard stop for idle tabs / marathon visits
  var MAX_AGE_MS = 6 * 3600 * 1000; // abandoned journeys expire silently

  function load() { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; } }
  function save(j) { try { localStorage.setItem(KEY, JSON.stringify(j)); } catch (e) {} }

  function record(label) {
    var j = load();
    if (!j || j.sent || !Array.isArray(j.actions) || j.actions.length >= 30) return;
    j.actions.push({ t: Math.max(0, Math.round((Date.now() - j.ts) / 1000)), a: String(label).slice(0, 120) });
    save(j);
  }

  var armed = false;
  function init() {
    if (armed) return;
    var j = load();
    if (!j || j.sent) return;
    if (Date.now() - j.ts > MAX_AGE_MS) { j.sent = true; save(j); return; }
    armed = true;

    // Returning long after the visit ended (a pagehide beacon that never
    // fired, e.g. iOS) — the journey is over; send it now and stop.
    if (Date.now() - j.ts >= CAP_MS) { send(); return; }

    // Page view (the results page's own script narrates that page's clicks).
    if (location.pathname.indexOf('/pages/wizard-results') !== 0) {
      record('viewed ' + location.pathname);
    }

    // Add-to-cart + checkout start: classic form posts + ajax adds. The fetch
    // patch only exists during an active journey and defers to original fetch.
    document.addEventListener('submit', function (e) {
      try {
        var f = e.target;
        var action = f && f.action ? String(f.action) : '';
        var toCheckout = e.submitter && e.submitter.name === 'checkout';
        if (action.indexOf('/cart/add') >= 0) {
          var v = f.querySelector('[name="id"]');
          record('added to cart' + (v && v.value ? ' (variant ' + v.value + ')' : ''));
        } else if (toCheckout || action.indexOf('/checkout') >= 0) {
          record('started checkout');
          send(); // last observable moment — theme JS doesn't run on checkout
        } else if (f) {
          try { sessionStorage.setItem(NAV_FLAG, '1'); } catch (e2) {} // form nav = staying on site
        }
      } catch (e3) {}
    }, true);
    var origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (u) {
        try {
          var url = typeof u === 'string' ? u : ((u && u.url) || '');
          if (String(url).indexOf('/cart/add') >= 0) {
            // Themes often submit the form AND ajax the same add — skip the
            // bare fetch record when a form add landed within the last 3s.
            var cur = load();
            var lastAct = cur && cur.actions && cur.actions[cur.actions.length - 1];
            var nowT = cur ? Math.round((Date.now() - cur.ts) / 1000) : 0;
            if (!(lastAct && String(lastAct.a).indexOf('added to cart') === 0 && nowT - lastAct.t <= 3)) {
              record('added to cart');
            }
          }
        } catch (e) {}
        return origFetch.apply(this, arguments);
      };
    }

    // Internal-link clicks set a flag so pagehide can tell "browsing on" from
    // "left the site" — only the latter sends the email early. A checkout
    // link ends the journey (no theme JS beyond that point).
    document.addEventListener('click', function (e) {
      try {
        var a = e.target.closest ? e.target.closest('a[href], button[name="checkout"]') : null;
        if (!a) return;
        var h = a.getAttribute('href') || '';
        if (a.name === 'checkout' || h.indexOf('/checkout') >= 0) {
          record('started checkout');
          send();
          return;
        }
        if (h.charAt(0) === '/' || h.charAt(0) === '#' || h.indexOf(location.origin) === 0) {
          sessionStorage.setItem(NAV_FLAG, '1');
        }
      } catch (e2) {}
    }, true);

    var sentHere = false;
    function send() {
      var cur = load();
      if (sentHere || !cur || cur.sent) return;
      sentHere = true;
      cur.sent = true;
      save(cur);
      var payload = JSON.stringify({
        type: 'wizard-complete',
        answers: cur.answers,
        actions: cur.actions,
        result_count: cur.result_count == null ? '' : String(cur.result_count),
        results_url: cur.results_url
      });
      // text/plain keeps it a "simple" request (sendBeacon can't preflight).
      try {
        if (navigator.sendBeacon &&
            navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain;charset=UTF-8' }))) return;
      } catch (e) {}
      try {
        var f = origFetch || window.fetch;
        f(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: payload, keepalive: true });
      } catch (e) {}
    }

    setTimeout(send, Math.max(1500, CAP_MS - (Date.now() - j.ts)));
    window.addEventListener('pagehide', function () {
      var internal = false;
      try { internal = sessionStorage.getItem(NAV_FLAG) === '1'; sessionStorage.removeItem(NAV_FLAG); } catch (e) {}
      // Browsing on within knobs.co → keep collecting (next page re-arms).
      // Leaving the site → the journey is over; send what we have.
      if (!internal) send();
    });
  }

  init();
  window.KWJourney = { init: init, record: record };

  /* ── Order attribution: stamp the cart with the hardware plan ─────────
     kw.plan.v1 is written by knobs-wizard.js at completion (90-day life) and by
     Sienna (knobs-advisor.js) when a plan is built or opened by its link. On every
     page load with a live plan, read the cart; if its attributes don't already
     carry THIS plan (new cart after a checkout, or never tagged), POST them.
     Shopify copies cart attributes onto the order as note_attributes → visible in
     admin + queryable (customAttributes). Costs two requests, and only for the
     ~0.2% of visitors who finished the wizard or the advisor. Dormant otherwise.
     2026-10-12: exposed as window.KWJourney.tagCart() → Promise<boolean> (true only
     when /cart/update.js answered 200), re-reads the record each run, carries
     hardware_plan_id, and keys "already tagged" on the plan id when there is one
     (source+date for legacy wizard records). The advisor awaits it before
     enabling its cart buttons; a pending passive run is cancelled by an explicit
     call so an older record can never overwrite the newer tag. */
  var PLAN_KEY = 'kw.plan.v1';
  var PLAN_MAX_AGE_MS = 90 * 24 * 3600 * 1000;
  var pendingTag = null;
  function readPlan() {
    var plan = null;
    try { plan = JSON.parse(localStorage.getItem(PLAN_KEY) || 'null'); } catch (e) {}
    if (!plan || !plan.ts || !plan.source) return null;
    if (Date.now() - plan.ts > PLAN_MAX_AGE_MS) { try { localStorage.removeItem(PLAN_KEY); } catch (e) {} return null; }
    return plan;
  }
  function tagCart() {
    if (pendingTag) { clearTimeout(pendingTag); pendingTag = null; }
    var plan = readPlan();
    var f = window.fetch;   // during an active journey this is the patched fetch, which defers to the original
    if (!plan || !f) return Promise.resolve(false);
    var date = new Date(plan.ts).toISOString().slice(0, 10);
    var answers = plan.answers || {};
    var summary = Object.keys(answers).map(function (k) { return k + '=' + answers[k]; }).join('; ').slice(0, 250);
    var attrs = {
      hardware_plan_source: plan.source,
      hardware_plan_date: date,
      hardware_plan_answers: summary || '(none)'
    };
    if (plan.plan_id) attrs.hardware_plan_id = String(plan.plan_id).slice(0, 40);
    return f('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return false;
        var cur = cart.attributes || {};
        var same = plan.plan_id ? cur.hardware_plan_id === attrs.hardware_plan_id
          : (cur.hardware_plan_source === attrs.hardware_plan_source && cur.hardware_plan_date === attrs.hardware_plan_date);
        if (same) return true;
        return f('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).then(function (r) { return r.status === 200; });
      })
      .catch(function () { return false; });
  }
  window.KWJourney.tagCart = tagCart;
  // Passive run on every page load (only does work when a plan record exists). Let the
  // page settle first; never compete with the shopper's own cart calls.
  if (readPlan()) {
    var passive = function () { pendingTag = setTimeout(function () { pendingTag = null; tagCart(); }, 1500); };
    if (document.readyState === 'complete') passive();
    else window.addEventListener('load', passive);
  }
})();
